import { Button, Input, Label, Modal } from '@iwoca/orion';
import { usePatchIntroducerPermissions } from 'lendingApi/lendingApiHooks';
import { FormProvider, useForm } from 'react-hook-form';
import { showSuccessToast } from '../../UserManagementToast';
import { UserPermissionsFields } from '../UserPermissionsFields/UserPermissionsFields';
import { TIntroducerListItem } from '../UsersTable.types';
import { diffPermissions, TEditUserFormValues } from './EditUserModal.helpers';
import styles from './EditUserModal.module.css';

type TProps = {
  user: TIntroducerListItem;
  isSelf: boolean;
  isOpen: boolean;
  onClose: () => void;
};

export const EditUserModal = ({ user, isSelf, isOpen, onClose }: TProps) => {
  const { mutate, isPending, isError, reset } = usePatchIntroducerPermissions();

  const methods = useForm<TEditUserFormValues>({
    defaultValues: { permissions: user.permissions },
  });

  const onSubmit = (values: TEditUserFormValues) => {
    reset();
    const changedPermissions = diffPermissions(
      user.permissions,
      values.permissions,
    );

    if (Object.keys(changedPermissions).length === 0) {
      onClose();
      return;
    }

    mutate(
      {
        introducerId: user.id,
        body: { data: changedPermissions },
      },
      {
        onSuccess: () => {
          showSuccessToast('User details updated');
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Edit user - ${user.first_name} ${user.last_name}`}
      size="large"
    >
      <FormProvider {...methods}>
        <form className={styles.form} onSubmit={methods.handleSubmit(onSubmit)}>
          <div className={styles.field}>
            <Label htmlFor="edit-user-email">Email</Label>
            <Input
              id="edit-user-email"
              value={user.email ?? ''}
              disabled
              readOnly
            />
          </div>
          <div className={styles.field}>
            <Label htmlFor="edit-user-phone">Phone</Label>
            <Input
              id="edit-user-phone"
              value={user.phone_number ?? ''}
              disabled
              readOnly
            />
          </div>
          <UserPermissionsFields isSelf={isSelf} />
          {isError && (
            <p className={styles.error}>
              Couldn’t save changes. Please try again.
            </p>
          )}
          <div className={styles.footer}>
            <Button
              variant="tertiary"
              type="button"
              onClick={onClose}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={isPending}>
              Save changes
            </Button>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
};
